import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);
dayjs.extend(timezone);

export const BOOKING_TIMEZONE = 'America/Denver';

export const denverNow = () => dayjs().tz(BOOKING_TIMEZONE);

export const combineDenverDateAndTime = (date: Date | string, time: Date | string) => {
  const ymd = dayjs(date).format('YYYY-MM-DD');
  const hm = dayjs(time).format('HH:mm');
  return dayjs.tz(`${ymd} ${hm}`, 'YYYY-MM-DD HH:mm', BOOKING_TIMEZONE);
};

export const minutesFromMidnight = (time: Date | string) => {
  const value = dayjs(time);
  return value.hour() * 60 + value.minute();
};

/** Earliest start minute today in Denver, rounded up to the next 15 minutes. */
export const earliestStartMinutes = (date: Date | string) => {
  const ymd = dayjs(date).format('YYYY-MM-DD');
  const now = denverNow();
  if (ymd !== now.format('YYYY-MM-DD')) {
    return 0;
  }

  const current = now.hour() * 60 + now.minute();
  return Math.ceil(current / 15) * 15;
};

export type BookingWindowIssue =
  | 'missing'
  | 'past-date'
  | 'past-start'
  | 'too-short'
  | 'wraps-midnight';

export const bookingWindowIssue = (
  date?: Date | string | null,
  startTime?: Date | string | null,
  endTime?: Date | string | null
): BookingWindowIssue | null => {
  if (!date || !startTime || !endTime) {
    return 'missing';
  }

  const ymd = dayjs(date).format('YYYY-MM-DD');
  if (ymd < denverNow().format('YYYY-MM-DD')) {
    return 'past-date';
  }

  const start = combineDenverDateAndTime(date, startTime);
  const end = combineDenverDateAndTime(date, endTime);
  if (!start.isValid() || !end.isValid()) {
    return 'missing';
  }

  if (start.isBefore(denverNow().startOf('minute'))) {
    return 'past-start';
  }

  if (end.format('YYYY-MM-DD') !== start.format('YYYY-MM-DD') || !end.isAfter(start)) {
    return 'wraps-midnight';
  }

  if (end.diff(start, 'minute') < 30) {
    return 'too-short';
  }

  return null;
};

export const bookingWindowMessage = (issue: BookingWindowIssue | null) => {
  switch (issue) {
    case 'past-date':
      return 'Choose today or a future date.';
    case 'past-start':
      return 'Start time must be later than the current time in Denver.';
    case 'too-short':
      return 'End time must be at least 30 minutes after the start time.';
    case 'wraps-midnight':
      return 'End time must be later the same day. Jobs cannot run past midnight.';
    case 'missing':
      return 'Choose a date, start time, and end time.';
    default:
      return '';
  }
};

const hasCoordinate = (value: unknown) =>
  value !== '' && value != null && Number.isFinite(Number(value));

/** Labels for required booking fields that are still empty. */
export const missingBookingFields = (input: {
  name?: string | null;
  description?: string | null;
  address?: string | null;
  latitude?: number | string | null;
  longitude?: number | string | null;
  recurring?: boolean;
  weekdays?: string[] | null;
  endMode?: string | null;
  visitCount?: number | string | null;
  expirationDate?: Date | string | null;
}) => {
  const missing: string[] = [];
  if (!String(input.name || '').trim()) {
    missing.push('a service name');
  }
  if (!String(input.description || '').trim()) {
    missing.push('a description');
  }
  if (
    !String(input.address || '').trim() ||
    !hasCoordinate(input.latitude) ||
    !hasCoordinate(input.longitude)
  ) {
    missing.push('a service address');
  }
  if (input.recurring) {
    if (!(input.weekdays || []).length) {
      missing.push('at least one day');
    }
    if (input.endMode === 'visits') {
      const visits = Number(input.visitCount);
      if (!(visits >= 1)) {
        missing.push('the number of visits');
      }
    } else if (!input.expirationDate) {
      missing.push('an end date');
    }
  }
  return missing;
};

export const missingBookingFieldsMessage = (missing: string[]) => {
  if (!missing.length) {
    return '';
  }
  if (missing.length === 1) {
    return `Enter ${missing[0]}.`;
  }
  return `Enter ${missing.slice(0, -1).join(', ')} and ${missing[missing.length - 1]}.`;
};
